// 공종 날짜 변경이 스케줄 기간(시작일·종료일)을 넘을 때 '함께 바뀝니다' 확인에 쓰는 판정·문구 (스케줄 공유 결정 3~5, T2 E-04·E-05).
// 순수 함수만 둔다 — 확인창은 usePeriodSyncConfirm 이 그린다.

/**
 * 스케줄 기간 연장에서 제외하는 공종 — 서버 TM_WORK_TYPE.PERIOD_INCLUDE_YN='N' (착공 전 선행 절차).
 * 서버 값과 같아야 하며 공종 마스터가 바뀌면 이 목록도 함께 바꾼다.
 */
export const PERIOD_EXCLUDED_WORK_TYPES: readonly string[] = ['CONSENT', 'NOTICE', 'ELEVATOR']

export type PeriodSyncItem = { workTypeCode: string; workTypeName: string; startDate: string; endDate: string }

export type PeriodSyncPlan = {
  /** 바뀔 종료일(YYYY-MM-DD). 종료일을 넘지 않으면 null */
  newEnd: string | null
  /** 바뀔 시작일(YYYY-MM-DD). 시작일 이전이 아니면 null */
  newStart: string | null
  oldEnd: string
  oldStart: string
  /** 종료일을 가장 크게 넘는 공종·시작일보다 가장 앞서는 공종의 이름 */
  endTaskName: string | null
  startTaskName: string | null
}

const iso = (s: string) => s.slice(0, 10)

/**
 * 변경된 공종 날짜가 스케줄 기간을 넘으면 계획을, 아니면 null 을 돌려준다.
 * 연장 제외 공종은 판정에서 뺀다. 스케줄 기간을 모르면(값 없음) null.
 */
export function computePeriodSyncPlan(
  site: { startDate?: string | null; endDate?: string | null } | null | undefined,
  items: PeriodSyncItem[],
): PeriodSyncPlan | null {
  const oldStart = site?.startDate ? iso(site.startDate) : ''
  const oldEnd = site?.endDate ? iso(site.endDate) : ''
  if (!oldStart || !oldEnd) return null
  let newEnd: string | null = null
  let newStart: string | null = null
  let endTaskName: string | null = null
  let startTaskName: string | null = null
  for (const it of items) {
    if (PERIOD_EXCLUDED_WORK_TYPES.includes(it.workTypeCode)) continue
    const s = iso(it.startDate)
    const e = iso(it.endDate)
    if (e > oldEnd && (newEnd === null || e > newEnd)) { newEnd = e; endTaskName = it.workTypeName }
    if (s < oldStart && (newStart === null || s < newStart)) { newStart = s; startTaskName = it.workTypeName }
  }
  if (newEnd === null && newStart === null) return null
  return { newEnd, newStart, oldEnd, oldStart, endTaskName, startTaskName }
}

const md = (d: string) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`

/** 확인창 제목 */
export function periodSyncTitle(plan: PeriodSyncPlan): string {
  if (plan.newEnd && plan.newStart) return '공사 기간도 함께 바뀝니다'
  return plan.newEnd ? '종료일도 함께 바뀝니다' : '시작일도 함께 바뀝니다'
}

/** 확인창 본문 문장들(한 문장씩) */
export function periodSyncLines(plan: PeriodSyncPlan): string[] {
  const lines: string[] = []
  if (plan.newEnd) {
    lines.push(`${plan.endTaskName ?? '공종'} 일정이 공사 종료일(${md(plan.oldEnd)})을 넘어요. 진행하면 공사 종료일이 ${md(plan.newEnd)}로 바뀌고, 공사완료 마커와 공유 링크 만료일도 함께 바뀝니다.`)
  }
  if (plan.newStart) {
    lines.push(`${plan.startTaskName ?? '공종'} 일정이 공사 시작일(${md(plan.oldStart)})보다 앞서요. 진행하면 공사 시작일이 ${md(plan.newStart)}로 바뀝니다.`)
  }
  return lines
}

/** 마일스톤 응답 warnings(서버 enum 이름) → 정보 문구. 모르는 값은 건너뛴다. */
const MARKER_WARNING_TEXT: Record<string, string> = {
  END_DATE_ADVANCED: '공사완료 마커를 앞당겨 공사 종료일이 짧아졌어요 · 종료일 뒤에 남은 공종이 있는지 확인해 주세요',
  MARKER_REMOVED_END_DATE_KEPT: '공사완료 마커를 지웠어요 · 공사 종료일은 그대로 유지돼요',
  MARKER_TYPE_CHANGED_END_DATE_KEPT: '마커 유형을 바꿨어요 · 공사 종료일은 그대로 유지돼요',
}

export function markerWarningText(warnings: unknown): string | null {
  if (!Array.isArray(warnings)) return null
  const texts = warnings
    .map(w => (typeof w === 'string' ? MARKER_WARNING_TEXT[w] : undefined))
    .filter((t): t is string => !!t)
  return texts.length > 0 ? texts.join(' / ') : null
}

/** 종료일 단축 저장 전 안내(C-3) — 단축이 아니면 null */
export function endDateShortenHint(oldEnd: string | null | undefined, newEnd: string | null | undefined): string | null {
  if (!oldEnd || !newEnd) return null
  return iso(newEnd) < iso(oldEnd)
    ? '종료일을 앞당기면 공사완료 마커와 공유 링크 만료일도 함께 바뀝니다. 종료일 뒤에 남는 공종이 있어도 저장은 막지 않아요.'
    : null
}
